import React, { ReactElement } from 'react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LockIcon from '@mui/icons-material/Lock';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

import { ActivityEntity, ActivityType } from '@/types';

type Props = {
  entity: ActivityEntity;
  type: ActivityType;
};

export const ActivityItemIcon = ({ entity, type }: Props): ReactElement => {
  const EngagementIconComponent = (
    <DescriptionOutlinedIcon
      sx={{
        fontSize: 24,
      }}
    />
  );

  // Matched before the entity branches: an analysis row is taxpayer-scoped, so it
  // would otherwise take the permanent-record glyph and read as a client-record
  // change rather than a finished AI run.
  if (type === ActivityType.analysisCompleted) {
    return <AutoAwesomeOutlinedIcon sx={{ fontSize: 24 }} />;
  }

  if (entity === ActivityEntity.taxpayer) {
    return (
      <>
        {EngagementIconComponent}
        <LockIcon
          sx={{
            fontSize: 14,
            position: 'absolute',
            bottom: -2,
            right: -2,
            backgroundColor: 'white',
            borderRadius: '50%',
          }}
        />
      </>
    );
  }

  if (entity === ActivityEntity.engagement) {
    if (type === ActivityType.fileUploaded) {
      return EngagementIconComponent;
    }

    if (type === ActivityType.taxQuestionnaireCompleted) {
      return <QuestionAnswerOutlinedIcon />;
    }

    if (type === ActivityType.bankQuestionnaireCompleted) {
      return <AccountBalanceOutlinedIcon />;
    }

    if (
      type === ActivityType.finalEfileAuthCompleted ||
      type === ActivityType.extensionEfileAuthCompleted
    ) {
      return <BorderColorOutlinedIcon />;
    }
  }

  if (entity === ActivityEntity.workflow) {
    if (type === ActivityType.fileUploaded) {
      return EngagementIconComponent;
    }

    // Matched explicitly rather than used as the workflow fallback: entity and type
    // are validated independently, so an unexpected workflow type must render no
    // glyph instead of a checkmark that contradicts the subtitle copy.
    if (type === ActivityType.workflowTaskCompleted) {
      return (
        <AssignmentTurnedInOutlinedIcon
          sx={{
            fontSize: 24,
          }}
        />
      );
    }
  }

  return <></>;
};
